import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  isSignal,
} from '@angular/core';
import { NavigationExtras, Router, RouterModule } from '@angular/router';
import { isDefinedNotEmpty, Store } from '@softline/core';
import * as ShellStore from '../../shell.store';
import { map, shareReplay, switchMap } from 'rxjs/operators';
import {
  BreakpointStore,
  IsDefinedNotEmptyPipe,
  SOFTLINE_FEATURE_BREAKPOINTS,
  UiCoreModule,
} from '@softline/ui-core';
import {
  asapScheduler,
  combineLatest,
  combineLatestWith,
  Observable,
  observeOn,
  of,
  timer,
} from 'rxjs';
import { CommonModule } from '@angular/common';
import {
  BackNavigationService,
  Command,
  CommandStore,
  MenuItemService,
  SOFTLINE_FEATURE_COMMANDS,
  SOFTLINE_FEATURE_SHELL,
  TitleService,
} from '@softline/application';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { MenuOutletComponent } from '../../../menu/menu-outlet/menu-outlet.component';
import { AuthenticationContextService } from '@softline/auth';
import { ShellAppearanceConfig } from '../../services/shell.service';

@Component({
  selector: 'soft-title-bar',
  templateUrl: './title-bar.component.html',
  styleUrls: ['./title-bar.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    RouterModule,
    UiCoreModule,
    MenuOutletComponent,
    IsDefinedNotEmptyPipe,
  ],
})
export class TitleBarComponent {
  private titleService = inject(TitleService);
  private menuItemsService = inject(MenuItemService);
  private authContextService = inject(AuthenticationContextService);

  readonly username = computed(() => {
    const appearance = this.appearance();
    if (typeof appearance === 'boolean') return this.authContextService.userName();

    let username = this.authContextService.userName();

    if (typeof appearance?.displayUsername === 'string')
      username = appearance?.displayUsername ?? '';
    else if (isSignal(appearance?.displayUsername)) {
      username = appearance?.displayUsername?.();
    }

    return isDefinedNotEmpty(username) ? username : null;
  });

  readonly appearance = input<ShellAppearanceConfig['titleBar']>();

  readonly showUsername = computed(() => {
    const appearance = this.appearance();
    const isBoolean = typeof appearance === 'boolean';
    return (
      (isBoolean && appearance) ||
      (!isBoolean &&
        typeof appearance?.displayUsername === 'boolean' &&
        appearance.displayUsername) ||
      (!isBoolean &&
        typeof appearance?.displayUsername === 'string' &&
        isDefinedNotEmpty(appearance.displayUsername))
    );
  });

  readonly dateTimeFormat = computed(() => {
    const appearance = this.appearance();
    const isBoolean = isDefinedNotEmpty(appearance) && typeof appearance === 'boolean';
    const shown = isBoolean
      ? !!appearance
      : isDefinedNotEmpty(appearance?.displayDateTime);

    if (!shown) return undefined;

    const dateFormat = 'dd.MM.yyyy';
    const timeFormat = 'HH:mm';

    if (!appearance) return undefined;

    if (
      isBoolean ||
      (typeof appearance.displayDateTime === 'boolean' && appearance.displayDateTime)
    ) {
      return `${dateFormat}, ${timeFormat}`;
    } else if (appearance && typeof appearance.displayDateTime === 'string') {
      return appearance.displayDateTime === 'date'
        ? 'dd.MM.yyyy'
        : appearance.displayDateTime === 'datetime'
          ? `${dateFormat}, ${timeFormat}`
          : `${timeFormat}`;
    }

    return undefined;
  });

  title = this.titleService.title;
  subtitle = this.titleService.subtitle;
  subtitleParams = this.titleService.params;

  readonly currentDateTime = toSignal(
    timer(0, 2_000).pipe(
      map(() => new Date()),
      shareReplay(1)
    ),
    { initialValue: new Date() }
  );

  leftCommands$: Observable<readonly Command[]> = this.store
    .observe(SOFTLINE_FEATURE_COMMANDS, CommandStore.getters.commands, 'top-menu-left')
    .pipe(observeOn(asapScheduler));

  mainMenuBadges$ = this.store
    .observe(SOFTLINE_FEATURE_COMMANDS, CommandStore.getters.commands, 'main-menu')
    .pipe(
      observeOn(asapScheduler),
      map((o) => o.map((p) => p.badge ?? of(undefined))),
      switchMap((o) => combineLatest(o)),
      map((o) =>
        o.filter((p) => isDefinedNotEmpty(p)).map((p) => p as unknown as string)
      ),
      map((o) => {
        const numberBadge = o.map((p) => Number.parseInt(p));
        if (numberBadge.length > 0 && numberBadge.every(Number.isInteger))
          return numberBadge.reduce((p, c) => p + c, 0);
        else if (o.length > 0) return '!';
        else return undefined;
      })
    );
  actionMenuBadges$ = this.store
    .observe(SOFTLINE_FEATURE_COMMANDS, CommandStore.getters.commands, 'action-menu')
    .pipe(
      observeOn(asapScheduler),
      map((o) => o.map((p) => p.badge ?? of(undefined))),
      switchMap((o) => combineLatest(o)),
      map((o) =>
        o.filter((p) => isDefinedNotEmpty(p)).map((p) => p as unknown as string)
      ),
      map((o) => {
        const numberBadge = o.map((p) => Number.parseInt(p));
        if (numberBadge.length > 0 && numberBadge.every(Number.isInteger))
          return numberBadge.reduce((p, c) => p + c, 0);
        else if (o.length > 0) return '!';
        else return undefined;
      })
    );

  rightCommands$: Observable<readonly Command[]> = this.store
    .observe(SOFTLINE_FEATURE_COMMANDS, CommandStore.getters.commands, 'top-menu-right')
    .pipe(observeOn(asapScheduler));

  isMobileScreen$: Observable<boolean> = this.store.observe(
    SOFTLINE_FEATURE_BREAKPOINTS,
    BreakpointStore.getters.match,
    null
  );

  hasActionMenuItems$ = toObservable(this.menuItemsService.menuItems).pipe(
    map((o) =>
      o
        .flat()
        .filter(
          (item) =>
            (item.outlet === 'action' || item.outlet === 'action-bottom') &&
            (item.visible ?? true)
        )
    ),
    map((o) => o.length > 0)
  );

  hasActionMenuItems = computed(() => {
    return (
      this.menuItemsService
        .menuItems()
        .filter(
          (item) =>
            (item.outlet === 'action' || item.outlet === 'action-bottom') &&
            (item.visible ?? true)
        ).length > 0
    );
  });

  hasSideActions$ = this.store
    .observe(SOFTLINE_FEATURE_COMMANDS, CommandStore.getters.hasCommands, 'action-menu')
    .pipe(
      combineLatestWith(this.hasActionMenuItems$),
      map(([a, b]) => a || b)
    );

  isOpen = false;

  constructor(
    private store: Store,
    private router: Router,
    public backNavigationService: BackNavigationService
  ) {}

  onClick(
    canExecute: boolean,
    routerLink: any[] | string | undefined,
    execute: (() => void) | undefined,
    routerLinkParams: NavigationExtras | undefined
  ): void {
    if (!canExecute) return;
    if (execute) execute();
    else if (routerLink)
      this.router.navigate(
        typeof routerLink === 'string' ? [routerLink] : routerLink,
        routerLinkParams
      );
  }

  maximizeSideMenu(): void {
    this.store.commit(SOFTLINE_FEATURE_SHELL, ShellStore.mutations.maximizeSideMenu);
  }

  maximizeActionMenu(): void {
    this.store.commit(SOFTLINE_FEATURE_SHELL, ShellStore.mutations.maximizeActionMenu);
  }
}
